import {
  type ColumnFiltersState,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  type Updater,
} from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';

import { DataTable, type DataTableColumnDef, type DataTableProps } from '@/components/DataTable';
import { FilterDetails } from '@/components/filters/FilterDetails';

import { useInstantSearchState } from './instantSearchContext';
import { InstantSearchSortOrder, type FilterOperatorValue, type QueryStateFilter } from './types';
import { isFilterOperatorRange } from './util/filter-util';

type InstantSearchDataTableColumnConfig = {
  field?: string;
  sortField?: string;
  filterField?: string;
};

export type InstantSearchDataTableColumnDef<
  TData extends object,
  TValue = unknown,
> = DataTableColumnDef<TData, TValue> & {
  instantSearch?: InstantSearchDataTableColumnConfig;
  columns?: InstantSearchDataTableColumnDef<TData>[];
};

export type InstantSearchDataTableProps<TData extends object> = Omit<
  DataTableProps<TData>,
  | 'columns'
  | 'data'
  | 'pagination'
  | 'rowCount'
  | 'manualPagination'
  | 'sorting'
  | 'onSortingChange'
  | 'manualSorting'
  | 'columnFilters'
  | 'onColumnFiltersChange'
  | 'manualFiltering'
> & {
  columns: InstantSearchDataTableColumnDef<TData>[];
  data?: TData[];
  totalResults?: number;
  pagination?: false;
  hideFilterDetails?: boolean;
  getFieldName?: (fieldId: string) => string;
};

type ColumnStateMaps = {
  columnIdToSortField: Map<string, string>;
  sortFieldToColumnId: Map<string, string>;
  columnIdToFilterField: Map<string, string>;
  filterFieldToColumnId: Map<string, string>;
  filterableFieldSet: Set<string>;
  fieldNameByField: Map<string, string>;
};

const resolveUpdater = <TValue,>(updater: Updater<TValue>, previous: TValue): TValue => {
  return typeof updater === 'function' ? (updater as (old: TValue) => TValue)(previous) : updater;
};

const isSortingEqual = (left: SortingState, right: SortingState) =>
  left.length === right.length &&
  left.every((leftSort, index) => {
    const rightSort = right[index];
    return rightSort != null && leftSort.id === rightSort.id && leftSort.desc === rightSort.desc;
  });

const getColumnId = <TData extends object>(
  column: InstantSearchDataTableColumnDef<TData>,
): string | undefined => {
  if (column.id != null) {
    return column.id;
  }

  if ('accessorKey' in column && column.accessorKey != null) {
    return String(column.accessorKey);
  }

  return undefined;
};

const getHeaderText = <TData extends object>(
  column: InstantSearchDataTableColumnDef<TData>,
): string | undefined => (typeof column.header === 'string' ? column.header : undefined);

const getSearchField = (
  columnId: string,
  instantSearch: InstantSearchDataTableColumnConfig | undefined,
) => instantSearch?.field ?? columnId;

const getSortField = (
  columnId: string,
  instantSearch: InstantSearchDataTableColumnConfig | undefined,
) => instantSearch?.sortField ?? getSearchField(columnId, instantSearch);

const getFilterField = (
  columnId: string,
  instantSearch: InstantSearchDataTableColumnConfig | undefined,
) => instantSearch?.filterField ?? getSearchField(columnId, instantSearch);

const collectColumnStateMaps = <TData extends object>(
  columns: InstantSearchDataTableColumnDef<TData>[],
): ColumnStateMaps => {
  const maps: ColumnStateMaps = {
    columnIdToSortField: new Map(),
    sortFieldToColumnId: new Map(),
    columnIdToFilterField: new Map(),
    filterFieldToColumnId: new Map(),
    filterableFieldSet: new Set(),
    fieldNameByField: new Map(),
  };

  const visit = (column: InstantSearchDataTableColumnDef<TData>) => {
    const columnId = getColumnId(column);

    if (columnId != null) {
      const sortField = getSortField(columnId, column.instantSearch);
      const filterField = getFilterField(columnId, column.instantSearch);
      const title = getHeaderText(column);

      maps.columnIdToSortField.set(columnId, sortField);
      maps.sortFieldToColumnId.set(sortField, columnId);
      maps.columnIdToFilterField.set(columnId, filterField);
      maps.filterFieldToColumnId.set(filterField, columnId);

      if (column.filterComponent != null) {
        maps.filterableFieldSet.add(filterField);
      }

      if (title != null) {
        maps.fieldNameByField.set(sortField, title);
        maps.fieldNameByField.set(filterField, title);
      }
    }

    if (Array.isArray(column.columns)) {
      column.columns.forEach(visit);
    }
  };

  columns.forEach(visit);
  return maps;
};

const isAppliedFilterValue = (filterValue: FilterOperatorValue): boolean => {
  if (filterValue == null) {
    return false;
  }

  if (!isFilterOperatorRange(filterValue) && Array.isArray(filterValue.operand)) {
    return filterValue.operand.length > 0;
  }

  return true;
};

const toQueryStateFilter = (
  field: string,
  filterValue: FilterOperatorValue,
): QueryStateFilter | null => {
  if (filterValue == null || !isAppliedFilterValue(filterValue)) {
    return null;
  }

  return {
    ...filterValue,
    field,
  } as QueryStateFilter;
};

const InstantSearchDataTable = <TData extends object>({
  columns,
  data,
  totalResults,
  pagination,
  getFieldName,
  hideFilterDetails = false,
  ...rest
}: InstantSearchDataTableProps<TData>) => {
  const { queryState, updateQueryState } = useInstantSearchState();
  const columnStateMaps = useMemo(() => collectColumnStateMaps(columns), [columns]);

  const sorting = useMemo<SortingState>(() => {
    if (!queryState.sortBy) {
      return [];
    }

    const columnId =
      columnStateMaps.sortFieldToColumnId.get(queryState.sortBy) ?? queryState.sortBy;
    return [
      {
        id: columnId,
        desc: queryState.sortOrder === InstantSearchSortOrder.DESC,
      },
    ];
  }, [columnStateMaps, queryState.sortBy, queryState.sortOrder]);

  const columnFilters = useMemo<ColumnFiltersState>(() => {
    return queryState.filters.flatMap((filter) => {
      const columnId = columnStateMaps.filterFieldToColumnId.get(filter.field);

      if (columnId == null) {
        return [];
      }

      return [
        {
          id: columnId,
          value: filter,
        },
      ];
    });
  }, [columnStateMaps, queryState.filters]);

  const paginationState = useMemo<PaginationState>(
    () => ({
      pageIndex: Math.max(queryState.page - 1, 0),
      pageSize: queryState.size,
    }),
    [queryState.page, queryState.size],
  );

  const handlePaginationChange = useCallback<OnChangeFn<PaginationState>>(
    (updater) => {
      const nextPaginationState = resolveUpdater(updater, paginationState);
      updateQueryState({
        page: nextPaginationState.pageIndex + 1,
        size: nextPaginationState.pageSize,
      });
    },
    [paginationState, updateQueryState],
  );

  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      const nextSorting = resolveUpdater(updater, sorting);
      if (isSortingEqual(sorting, nextSorting)) {
        return;
      }

      const nextSort = nextSorting[0];

      if (nextSort == null) {
        updateQueryState({ sortBy: '', page: 1 });
        return;
      }

      updateQueryState({
        sortBy: columnStateMaps.columnIdToSortField.get(nextSort.id) ?? nextSort.id,
        sortOrder: nextSort.desc ? InstantSearchSortOrder.DESC : InstantSearchSortOrder.ASC,
        page: 1,
      });
    },
    [columnStateMaps, sorting, updateQueryState],
  );

  const handleColumnFiltersChange = useCallback<OnChangeFn<ColumnFiltersState>>(
    (updater) => {
      const nextColumnFilters = resolveUpdater(updater, columnFilters);
      const globalFilters = queryState.filters.filter(
        (filter) => !columnStateMaps.filterableFieldSet.has(filter.field),
      );

      const tableFilters = nextColumnFilters
        .map((filter) =>
          toQueryStateFilter(
            columnStateMaps.columnIdToFilterField.get(filter.id) ?? filter.id,
            filter.value as FilterOperatorValue,
          ),
        )
        .filter((filter): filter is QueryStateFilter => filter != null);

      updateQueryState({
        filters: globalFilters.concat(tableFilters),
        page: 1,
      });
    },
    [columnFilters, columnStateMaps, queryState.filters, updateQueryState],
  );

  const resolvedPagination = useMemo<DataTableProps<TData>['pagination']>(() => {
    if (pagination === false || totalResults == null) {
      return false;
    }

    return {
      paginationState,
      onPaginationChange: handlePaginationChange,
    };
  }, [handlePaginationChange, pagination, paginationState, totalResults]);

  const getDefaultFieldName = useCallback(
    (fieldId: string) => columnStateMaps.fieldNameByField.get(fieldId) ?? fieldId,
    [columnStateMaps],
  );

  return (
    <div data-testid="instant-search-table" className="flex flex-col">
      {!hideFilterDetails && (
        <FilterDetails
          className="relative -top-2.5 self-end"
          getFieldName={getFieldName ?? getDefaultFieldName}
        />
      )}
      <DataTable<TData>
        columns={columns}
        data={data ?? []}
        rowCount={totalResults}
        pagination={resolvedPagination}
        manualPagination={resolvedPagination !== false}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        manualSorting
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        manualFiltering
        {...rest}
      />
    </div>
  );
};

export default InstantSearchDataTable;
